// Govee H5074 / H5075 adapter — advertising-based, no pairing.
//
// H5075 (GVH5075_*) layout in manufacturer data (company id 0xEC88):
//   [0]       frame marker (0x00)
//   [1..3]    packed 24-bit unsigned: value = temp_milliC + humidity*1000 encoded as
//             `raw = int(t*1000) + int(h*1000)` — see:
//             https://github.com/neilsheps/GoveeTemperatureAndHumidity
//             Temperature in C = raw / 10000.0, Humidity % = (raw % 1000) / 10.
//             Negative temps: raw = (temp_c * 10000) + 0x800000.
//   [4]       battery percent (uint8 0..100)
//   [5]       0x00 padding
//
// H5074 layout (company id 0xEC88):
//   [0]       0x00
//   [1..2]    temperature (int16 LE, value / 100)
//   [3..4]    humidity    (uint16 LE, value / 100)
//   [5]       battery percent
//
// TODO(verify): H5102/H5051/H5179 use different company ids and different layouts;
// we currently only target H5074 / H5075. Local name prefix is used as the
// model discriminator.

import type { ScanResult } from '@capacitor-community/bluetooth-le';
import {
  GenericBleProbeAdapter,
  type AdvertisingReading,
} from './generic-ble.js';
import type { ProbeVendor } from '@trackeat/shared';

const GOVEE_COMPANY_ID = 0xec88;

interface Decoded {
  temperature_c: number;
  humidity_pct: number | null;
  battery_pct: number | null;
}

function decode5075(bytes: Uint8Array): Decoded | null {
  if (bytes.length < 6) return null;
  const raw = (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
  const negative = (raw & 0x800000) !== 0;
  const magnitude = raw & 0x7fffff;
  const temperature_c = (negative ? -magnitude : magnitude) / 10000;
  const humidity_pct = (magnitude % 1000) / 10;
  return {
    temperature_c,
    humidity_pct,
    battery_pct: bytes[4] <= 100 ? bytes[4] : null,
  };
}

function decode5074(bytes: Uint8Array): Decoded | null {
  if (bytes.length < 6) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tempRaw = view.getInt16(1, true);
  const humRaw = view.getUint16(3, true);
  const battery = view.getUint8(5);
  return {
    temperature_c: tempRaw / 100,
    humidity_pct: humRaw / 100,
    battery_pct: battery <= 100 ? battery : null,
  };
}

export class GoveeAdapter extends GenericBleProbeAdapter {
  readonly vendor: ProbeVendor = 'govee';

  protected parseAdvertisement(result: ScanResult): AdvertisingReading | null {
    const name = (result.localName ?? result.device?.name ?? '').toUpperCase();
    const bytes = this.manufacturerBytes(result, GOVEE_COMPANY_ID);
    if (!bytes) return null;

    let decoded: Decoded | null = null;
    let modelHint = 'govee';
    if (name.startsWith('GVH5075')) {
      modelHint = 'H5075';
      decoded = decode5075(bytes);
    } else if (name.startsWith('GVH5074')) {
      modelHint = 'H5074';
      decoded = decode5074(bytes);
    } else {
      // Unknown Govee model — try 5075 layout first (most common), then 5074.
      decoded = decode5075(bytes) ?? decode5074(bytes);
    }
    if (!decoded) return null;
    if (decoded.temperature_c < -40 || decoded.temperature_c > 100) return null;

    const deviceId = result.device?.deviceId ?? result.device?.name ?? '';
    if (!deviceId) return null;

    return {
      reading: {
        device_id: deviceId,
        temperature_c: decoded.temperature_c,
        humidity_pct: decoded.humidity_pct,
        battery_pct: decoded.battery_pct,
        received_at: this.nowIso(),
      },
      probe: {
        device_id: deviceId,
        name: result.device?.name ?? 'Govee',
        vendor: 'govee',
        model_hint: modelHint,
        rssi: result.rssi ?? null,
        last_seen: this.nowIso(),
      },
    };
  }
}
